/*
 * Zoom model for the viewer. Pure functions only — the component owns the
 * zoom state and measures the container; these compute scales and step
 * targets. Scale is a multiplier on the pdf.js render (1 = 100%, page point
 * == CSS pixel before DPR). Fit modes derive a scale from the available
 * container box and the (largest, rotation-aware) page dimensions so no page
 * overflows horizontally.
 */

export type ZoomMode = 'fit-width' | 'fit-page' | 'custom';

export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 5;

/** Discrete steps the +/- buttons snap to (also used by keyboard Ctrl +/-). */
const STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 1, 1.25, 1.5, 2, 3, 4, 5] as const;

const EPS = 1e-6;

export function clampZoom(z: number): number {
  if (Number.isNaN(z)) return 1;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
}

/** Next step strictly larger than `z` (clamped); stays at max if already there. */
export function nextZoomIn(z: number): number {
  const up = STEPS.find((s) => s > z + EPS);
  return clampZoom(up ?? ZOOM_MAX);
}

/** Next step strictly smaller than `z` (clamped); stays at min if already there. */
export function nextZoomOut(z: number): number {
  const down = [...STEPS].reverse().find((s) => s < z - EPS);
  return clampZoom(down ?? ZOOM_MIN);
}

/** Scale that makes a page of `pageWidthPt` exactly fill `availWidthPx`. */
export function fitWidthScale(pageWidthPt: number, availWidthPx: number): number {
  if (pageWidthPt <= 0 || availWidthPx <= 0) return 1;
  return clampZoom(availWidthPx / pageWidthPt);
}

/** Scale that fits a whole page inside the available box (min of W/H fit). */
export function fitPageScale(
  pageWidthPt: number,
  pageHeightPt: number,
  availWidthPx: number,
  availHeightPx: number,
): number {
  if (pageWidthPt <= 0 || pageHeightPt <= 0 || availWidthPx <= 0 || availHeightPx <= 0) return 1;
  return clampZoom(Math.min(availWidthPx / pageWidthPt, availHeightPx / pageHeightPt));
}

/** Format a scale as a percentage label, e.g. 1.25 → "125%". */
export function zoomLabel(z: number): string {
  return `${Math.round(z * 100)}%`;
}
